import api from '../api/client'

class UsageTracker {
  constructor() {
    this.sessionStart = new Date()
    this.currentPage = null
    this.lastActivityTime = new Date()
    this.deviceInfo = this.captureDeviceInfo()
    this.geoLocation = null
    this.checkLocationPermission()
  }

  captureDeviceInfo() {
    const ua = navigator.userAgent
    const browserInfo = this.parseBrowser(ua)
    const osInfo = this.parseOS(ua)

    return {
      user_agent: ua,
      device_type: this.getDeviceType(),
      browser_name: browserInfo.name,
      browser_version: browserInfo.version,
      os_name: osInfo.name,
      os_version: osInfo.version,
      language: navigator.language || 'en',
      app_version: this.getAppVersion(),
    }
  }

  parseBrowser(ua) {
    let browserName = 'Unknown'
    let browserVersion = 'Unknown'

    if (ua.indexOf('Chrome') > -1 && ua.indexOf('Chromium') === -1) {
      browserName = 'Chrome'
      browserVersion = ua.match(/Chrome\/([0-9]+)/)?.[1] || 'Unknown'
    } else if (ua.indexOf('Safari') > -1) {
      browserName = 'Safari'
      browserVersion = ua.match(/Version\/([0-9]+)/)?.[1] || 'Unknown'
    } else if (ua.indexOf('Firefox') > -1) {
      browserName = 'Firefox'
      browserVersion = ua.match(/Firefox\/([0-9]+)/)?.[1] || 'Unknown'
    } else if (ua.indexOf('Edge') > -1) {
      browserName = 'Edge'
      browserVersion = ua.match(/Edge\/([0-9]+)/)?.[1] || 'Unknown'
    }

    return { name: browserName, version: browserVersion }
  }

  parseOS(ua) {
    let osName = 'Unknown'
    let osVersion = 'Unknown'

    if (ua.indexOf('Windows') > -1) {
      osName = 'Windows'
      osVersion = ua.match(/Windows NT ([0-9.]+)/)?.[1] || 'Unknown'
    } else if (ua.indexOf('Mac') > -1) {
      osName = 'macOS'
      osVersion = ua.match(/Mac OS X ([0-9_]+)/)?.[1]?.replace(/_/g, '.') || 'Unknown'
    } else if (ua.indexOf('Android') > -1) {
      osName = 'Android'
      osVersion = ua.match(/Android ([0-9.]+)/)?.[1] || 'Unknown'
    } else if (ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) {
      osName = 'iOS'
      osVersion = ua.match(/Version\/([0-9.]+)/)?.[1] || 'Unknown'
    } else if (ua.indexOf('Linux') > -1) {
      osName = 'Linux'
      osVersion = 'Unknown'
    }

    return { name: osName, version: osVersion }
  }

  getDeviceType() {
    const ua = navigator.userAgent.toLowerCase()
    if (ua.indexOf('mobile') > -1 || ua.indexOf('iphone') > -1) return 'mobile'
    if (ua.indexOf('tablet') > -1 || ua.indexOf('ipad') > -1) return 'tablet'
    return 'desktop'
  }

  getAppVersion() {
    const metaTag = document.querySelector('meta[name="app-version"]')
    return metaTag?.content || '1.0.0'
  }

  checkLocationPermission() {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.geoLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }
          this.fetchCityFromCoords(position.coords.latitude, position.coords.longitude)
        },
        (error) => {
          console.log('Geolocation not available:', error)
        }
      )
    }
  }

  async fetchCityFromCoords(lat, lon) {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`,
        { timeout: 5000 }
      )
      const data = await response.json()
      if (data.address) {
        this.geoLocation.city = data.address.city || data.address.town || data.address.village || 'Unknown'
        this.geoLocation.country = data.address.country || 'Unknown'
        this.geoLocation.region = data.address.state || 'Unknown'
      }
    } catch (error) {
      console.log('Failed to fetch location data:', error)
    }
  }

  trackPageView(pagePath, pageTitle) {
    this.currentPage = {
      path: pagePath,
      title: pageTitle,
      viewStartTime: new Date(),
    }
  }

  getClientIP() {
    return null
  }

  async sendUsageLog(userEmail, userHashId, duration) {
    if (!userEmail) return

    const logData = {
      user_email: userEmail,
      user_hashid: userHashId || '',
      page_path: this.currentPage?.path || window.location.pathname,
      page_title: this.currentPage?.title || document.title,
      session_start: this.sessionStart.toISOString(),
      session_end: new Date().toISOString(),
      duration_seconds: duration || Math.round((new Date() - this.sessionStart) / 1000),
      referrer: document.referrer || null,
      ...this.deviceInfo,
      ...(this.geoLocation || {}),
    }

    try {
      await api.post('/usage-logs/', logData)
    } catch (error) {
      console.error('Failed to send usage log:', error)
    }
  }

  startSession() {
    this.sessionStart = new Date()
  }

  endSession(userEmail, userHashId) {
    this.sendUsageLog(userEmail, userHashId)
  }
}

export default new UsageTracker()
